import { useState, useRef, useEffect, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import { ArrowUpRight } from 'lucide-react';
import type { SuggestionResponse, QueryResponse, SuggestionChip, ProofItem } from '../../lib/types';
import logger from '../../lib/logger';
import { getApiBaseUrl } from '../../lib/utils';
import { useAnimateIn, useStaggeredAnimateIn } from '@/lib/useAnimateIn';
import UserMessage from '../ui/UserMessage';
import ThoughtDots from '../ui/ThoughtDots';
import AIResponse from '../ui/AIResponse';
import WorkCards from '../ui/WorkCards';
import SuggestedResponses from '../ui/SuggestedResponses';

interface ChatViewProps {
  setCurrentView: (view: number | ((prev: number) => number)) => void;
  selectedOptions?: string[];
}

export default function ChatView({ setCurrentView, selectedOptions = [] }: ChatViewProps) {
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState<string[]>([]);
  const [suggestedResponses, setSuggestedResponses] = useState<Array<{label: string; row_id: string}>>([]);
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [conversationHistory, setConversationHistory] = useState<Array<{role: 'user' | 'assistant'; content: string}>>([]);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollingAreaRef = useRef<HTMLDivElement>(null);
  const thoughtDotsContainerRef = useRef<HTMLDivElement | null>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Title animation (other animations to be added one-by-one)
  const { style: titleStyle } = useAnimateIn({ delay: 100 });
  
  // UserMessage animation
  const { style: userMessageStyle } = useAnimateIn({ delay: 300 });

  // First AI response typing animation
  const firstAIResponseText = "If you tell me a bit more, I can show you related work we've done or some of our thinking in these areas.";
  const [typedText, setTypedText] = useState('');
  const [showInput, setShowInput] = useState(false);
  
  useEffect(() => {
    // Start typing after UserMessage animation completes (300ms delay + ~400ms animation = ~700ms)
    const startTypingDelay = 700;
    const minTypingSpeed = 3; // milliseconds per character
    const maxTypingSpeed = 20; // milliseconds per character
    
    let timeoutIds: NodeJS.Timeout[] = [];
    
    const timeoutId = setTimeout(() => {
      let currentIndex = 0;
      
      const typeNextChar = () => {
        if (currentIndex < firstAIResponseText.length) {
          setTypedText(firstAIResponseText.slice(0, currentIndex + 1));
          currentIndex++;
          
          // Random delay between min and max typing speed
          const randomDelay = Math.floor(Math.random() * (maxTypingSpeed - minTypingSpeed + 1)) + minTypingSpeed;
          const nextTimeout = setTimeout(typeNextChar, randomDelay);
          timeoutIds.push(nextTimeout);
        } else {
          // Typing finished, show input immediately
          setShowInput(true);
        }
      };
      
      typeNextChar();
    }, startTypingDelay);

    return () => {
      clearTimeout(timeoutId);
      timeoutIds.forEach(id => clearTimeout(id));
    };
  }, [firstAIResponseText]);

  // Input box fade-in animation (opacity only, ease-out)
  const [inputVisible, setInputVisible] = useState(false);
  const [showSuggestedResponses, setShowSuggestedResponses] = useState(false);
  
  useEffect(() => {
    if (showInput) {
      // Use requestAnimationFrame to ensure DOM is ready before triggering animation
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setInputVisible(true);
        });
      });
    }
  }, [showInput]);

  // Auto-resize textarea based on content
  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Heights in rem for better accessibility
    const minHeightRem = 3.5; // Minimum height for mobile (3.5rem = 56px)
    const maxHeightRem = 12.5; // Maximum height before scrolling (12.5rem = 200px)
    const desktopMinHeightRem = 4.25; // Minimum height for desktop (4.25rem = 68px)
    
    // Convert rem to px for calculations
    const pxPerRem = 16;
    const minHeight = minHeightRem * pxPerRem;
    const maxHeight = maxHeightRem * pxPerRem;
    const desktopMinHeight = desktopMinHeightRem * pxPerRem;
    
    // Use appropriate min height based on screen size
    const currentMinHeight = window.innerWidth >= 1024 ? desktopMinHeight : minHeight;
    
    // Calculate padding for single line (centered)
    const lineHeight = parseFloat(getComputedStyle(textarea).lineHeight) || 24;
    const singleLinePaddingTop = (currentMinHeight - lineHeight) / 2;
    
    // Set to minimum height with single-line padding to measure if content fits
    textarea.style.height = `${currentMinHeight}px`;
    textarea.style.paddingTop = `${singleLinePaddingTop}px`;
    textarea.style.paddingBottom = `${singleLinePaddingTop}px`;
    
    // Get the scrollHeight - if it's greater than minHeight, text has wrapped
    const scrollHeight = textarea.scrollHeight;
    
    // Only grow if text actually wraps to a new line (scrollHeight exceeds container)
    if (scrollHeight > currentMinHeight) {
      // Text has wrapped, grow to accommodate it (up to maxHeight)
      // Reset to auto to get accurate measurement, then constrain
      textarea.style.height = 'auto';
      textarea.style.paddingTop = '0.75rem';
      textarea.style.paddingBottom = '0.75rem';
      const fullScrollHeight = textarea.scrollHeight;
      const newHeight = Math.min(fullScrollHeight, maxHeight);
      textarea.style.height = `${newHeight}px`;
    } else {
      // Text fits on one line, keep at minimum height with centered padding
      textarea.style.height = `${currentMinHeight}px`;
      textarea.style.paddingTop = `${singleLinePaddingTop}px`;
      textarea.style.paddingBottom = `${singleLinePaddingTop}px`;
    }
  }, []);

  // Auto-focus textarea when it becomes visible
  useEffect(() => {
    if (inputVisible && textareaRef.current) {
      // Small delay to ensure the input is fully rendered and visible
      const focusTimer = setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);
      return () => clearTimeout(focusTimer);
    }
  }, [inputVisible]);

  useEffect(() => {
    if (!inputVisible) return;
    adjustTextareaHeight();
  }, [message, inputVisible, adjustTextareaHeight]);

  // Handle window resize to recalculate height
  useEffect(() => {
    if (!inputVisible) return;
    
    const handleResize = () => {
      adjustTextareaHeight();
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [inputVisible, adjustTextareaHeight]);
  
  // Show suggested responses after input animation completes (500ms)
  useEffect(() => {
    if (inputVisible) {
      const timer = setTimeout(() => {
        setShowSuggestedResponses(true);
      }, 500); // Wait for input animation to complete
      
      return () => clearTimeout(timer);
    }
  }, [inputVisible]);
  
  const inputStyle: React.CSSProperties = {
    transition: 'opacity 500ms ease-out',
    opacity: inputVisible ? 1 : 0
  };

  // Submit button scale animation
  const [buttonVisible, setButtonVisible] = useState(false);

  useEffect(() => {
    if (inputVisible) {
      // Ensure initial state is rendered first, then start animation
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            setButtonVisible(true);
          }, 200);
        });
      });
    }
  }, [inputVisible]);

  const buttonStyle: React.CSSProperties = {
    transition: 'transform 300ms ease-out',
    transform: buttonVisible ? 'scale(1)' : 'scale(0)',
    transformOrigin: 'center',
  };
  
  // Staggered animation for suggested response buttons
  const { getItemStyle: getSuggestedButtonStyle } = useStaggeredAnimateIn({
    delay: 0,
    staggerDelay: 40,
    duration: 300,
    autoStart: showSuggestedResponses
  });

  // Scroll button fade-in animation
  const [scrollButtonVisible, setScrollButtonVisible] = useState(false);
  
  useEffect(() => {
    if (showScrollButton) {
      // Add delay before showing the button
      const timer = setTimeout(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setScrollButtonVisible(true);
          });
        });
      }, 500); // 500ms delay
      return () => clearTimeout(timer);
    } else {
      setScrollButtonVisible(false);
    }
  }, [showScrollButton]);
  
  const scrollButtonStyle: React.CSSProperties = {
    transition: 'opacity 300ms ease-out, transform 300ms ease-out',
    opacity: scrollButtonVisible ? 1 : 0,
    transform: scrollButtonVisible 
      ? 'translateX(-50%) translateY(0)' 
      : 'translateX(-50%) translateY(1.25rem)'
  };

  // Fetch suggested responses function (to be called manually)
  const fetchSuggestedResponses = async () => {
    const API_BASE_URL = getApiBaseUrl();
    const API_KEY = process.env.NEXT_PUBLIC_API_KEY;
    
    if (!API_BASE_URL || !API_KEY) {
      logger.error('Missing API configuration. Please set NEXT_PUBLIC_API_URL and NEXT_PUBLIC_API_KEY environment variables.');
      // Fallback to mock suggestions
      const mockSuggestions = [
        { label: "Our growth has slowed", row_id: "fallback_1" },
        { label: "My customer experience feels outdated", row_id: "fallback_2" },
        { label: "My data is not being put to use", row_id: "fallback_3" }
      ];
      setSuggestedResponses(mockSuggestions);
      return;
    }
    
    // Map selected options to challenge categories
    const mapOptionsToChallenges = (options: string[]): string[] => {
      logger.info('Selected options received:', options);
      
      const optionToChallengeMap: Record<string, string> = {
        'Better understand my audiences': 'deep-customer-understanding',
        'Modernize my CX': 'modern-experience', 
        'Improve loyalty': 'retention-loyalty',
        'Integrate AI': 'ai-integration-adoption',
        'Harness my data': 'data-optimization',
        'Create cohesive content': 'omnichannel-content'
      };
      
      const mappedChallenges = options
        .map(option => optionToChallengeMap[option])
        .filter(Boolean); // Remove any undefined mappings
      
      logger.info('Final mapped challenges:', mappedChallenges);
      return mappedChallenges;
    };
    
    // Build query parameters
    const challenges = mapOptionsToChallenges(selectedOptions);
    const queryParams = new URLSearchParams();
    
    if (challenges.length > 0) {
      queryParams.append('challenges', challenges.join(','));
    }
    queryParams.append('limit', '4');
    
    const apiUrl = `${API_BASE_URL}/suggest?${queryParams.toString()}`;
    logger.info('Final API URL:', apiUrl);
    
    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'X-API-Key': API_KEY,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }
      
      const data: SuggestionResponse = await response.json();
      
      // Extract chips from the API response and store both label and row_id
      const allChips: SuggestionChip[] = data.suggestions.flatMap((suggestion) => 
        suggestion.chips.map((chip) => ({ label: chip.label, row_id: chip.row_id }))
      );
      
      setSuggestedResponses(allChips);
    } catch (error) {
      logger.error('API Error:', error);
      // Fallback to mock suggestions on error
      const mockSuggestions = [
        { label: "Our growth has slowed", row_id: "fallback_1" },
        { label: "My customer experience feels outdated", row_id: "fallback_2" },
        { label: "My data is not being put to use", row_id: "fallback_3" }
      ];
      setSuggestedResponses(mockSuggestions);
    }
  };

  // Query API function
  const submitQuery = async (userQuery: string, rowIds: string[]) => {
    const API_BASE_URL = getApiBaseUrl();
    const API_KEY = process.env.NEXT_PUBLIC_API_KEY;
    
    if (!API_BASE_URL || !API_KEY) {
      logger.error('Missing API configuration. Please set NEXT_PUBLIC_API_URL and NEXT_PUBLIC_API_KEY environment variables.');
      return;
    }
    
    try {
      // Get last 10 messages from conversation history
      const recentHistory = conversationHistory.slice(-10);
      
      const requestBody = {
        query: userQuery,
        row_ids: rowIds,
        conversationHistory: recentHistory
      };
      
      logger.info('Submitting query:', requestBody);
      
      const response = await fetch(`${API_BASE_URL}/query`, {
        method: 'POST',
        headers: {
          'X-API-Key': API_KEY,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }
      
      const data: QueryResponse = await response.json();
      logger.info('Query response received');
      
      // Add user message to conversation history
      setConversationHistory(prev => [...prev, { role: 'user', content: userQuery }]);
      
      // Add assistant response to conversation history
      setConversationHistory(prev => [...prev, { role: 'assistant', content: data.answer.body }]);
      
      // Display the AI response
      appendAIResponse(data.answer.body);
      
      // Display work cards if provided
      if (data.proofs && data.proofs.length > 0) {
        const workCards = data.proofs.map((proof: ProofItem) => {
          // Extract just the filename from the imageSrc if it's a full URL
          let imageSrc = proof.imageSrc;
          if (imageSrc.includes('/')) {
            imageSrc = imageSrc.split('/').pop() || imageSrc;
          }
                    // Use content_link for articles, href for case-studies
          const link = proof.content_type === 'article' ? proof.content_link : proof.href;
          
          return {
            href: link,
            imageSrc: imageSrc,
            imageAlt: proof.imageAlt,
            title: proof.title,
            linkText: proof.linkText
          };
        });
        appendWorkCards(workCards);
      }
      
    } catch (error) {
      logger.error('Query API Error:', error);
      // Fallback response
      appendAIResponse('I apologize, but I encountered an error processing your request. Please try again.');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;
    
    // Add user message to messages array
    setMessages([...messages, message]);
    
    // Display user message in chat
    let userMessageContainer: HTMLDivElement | null = null;
    if (scrollingAreaRef.current) {
      const container = document.createElement('div');
      container.style.width = '100%';
      container.style.display = 'flex';
      container.style.justifyContent = 'flex-end';
      scrollingAreaRef.current.appendChild(container);
      userMessageContainer = container;
      
      const root = createRoot(container);
      root.render(<UserMessage submittedMessage={message} />);
      
      // Scroll to show the user's question
      setTimeout(() => {
        if (scrollingAreaRef.current && userMessageContainer) {
          const scrollingAreaRect = scrollingAreaRef.current.getBoundingClientRect();
          const containerRect = userMessageContainer.getBoundingClientRect();
          const relativeTop = containerRect.top - scrollingAreaRect.top + scrollingAreaRef.current.scrollTop;
          
          scrollingAreaRef.current.scrollTo({
            top: relativeTop,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
    
    // Show ThoughtDots while waiting for response
    setTimeout(() => {
      appendThoughtDots();
    }, 150);
    
    // Submit query to API with only the typed message (no selected suggestions)
    submitQuery(message, []);
    
    // Clear the input
    setMessage('');
    setShowSuggestions(false);
  };

  const handleSuggestionClick = (suggestion: {label: string; row_id: string}) => {
    // Add the row_id to selectedRowIds and immediately submit query
    setSelectedRowIds(prev => [...prev, suggestion.row_id]);
    
    // Display the user's selection
    let userMessageContainer: HTMLDivElement | null = null;
    if (scrollingAreaRef.current) {
      const container = document.createElement('div');
      container.style.width = '100%';
      container.style.display = 'flex';
      container.style.justifyContent = 'flex-end';
      scrollingAreaRef.current.appendChild(container);
      userMessageContainer = container;
      
      const root = createRoot(container);
      root.render(<UserMessage submittedMessage={suggestion.label} />);
      
      // Scroll to show the user's question
      setTimeout(() => {
        if (scrollingAreaRef.current && userMessageContainer) {
          const scrollingAreaRect = scrollingAreaRef.current.getBoundingClientRect();
          const containerRect = userMessageContainer.getBoundingClientRect();
          const relativeTop = containerRect.top - scrollingAreaRect.top + scrollingAreaRef.current.scrollTop;
          
          scrollingAreaRef.current.scrollTo({
            top: relativeTop,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
    
    // Show ThoughtDots while waiting for response
    setTimeout(() => {
      appendThoughtDots();
    }, 150);
    
    // Submit query immediately with the suggestion
    submitQuery(suggestion.label, [...selectedRowIds, suggestion.row_id]);
    
    // Hide suggestions after selection
    setShowSuggestions(false);
  };

  // Load suggestions when selectedOptions are provided (triggered by Continue button)
  useEffect(() => {
    if (selectedOptions.length > 0 && suggestedResponses.length === 0) {
      fetchSuggestedResponses();
    }
  }, [selectedOptions]);

  // Check if user has scrolled away from bottom
  const handleScroll = useCallback(() => {
    if (!scrollingAreaRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = scrollingAreaRef.current;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
    
    setShowScrollButton(!isNearBottom);
    
    // Show custom scrollbar when scrolling
    setIsScrolling(true);
    
    // Clear existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    // Hide scrollbar after scrolling stops (300ms delay)
    scrollTimeoutRef.current = setTimeout(() => {
      setIsScrolling(false);
    }, 300);
  }, []);

  // Scroll to bottom function
  const scrollToBottom = () => {
    if (!scrollingAreaRef.current) return;
    
    scrollingAreaRef.current.scrollTo({
      top: scrollingAreaRef.current.scrollHeight,
      behavior: 'smooth'
    });
  };

  // Add scroll listener
  useEffect(() => {
    const scrollArea = scrollingAreaRef.current;
    if (!scrollArea) return;

    scrollArea.addEventListener('scroll', handleScroll);
    return () => {
      scrollArea.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [handleScroll]);

  /**
   * Appends ThoughtDots to show the AI is thinking
   * @returns The container element for the ThoughtDots
   */
  const appendThoughtDots = useCallback(() => {
    if (!scrollingAreaRef.current) return null;

    // Remove existing ThoughtDots if any
    if (thoughtDotsContainerRef.current && thoughtDotsContainerRef.current.parentNode) {
      thoughtDotsContainerRef.current.parentNode.removeChild(thoughtDotsContainerRef.current);
    }

    // Create a new div container for ThoughtDots
    const container = document.createElement('div');
    container.style.width = '100%';
    container.style.display = 'flex';
    container.style.justifyContent = 'flex-start';
    scrollingAreaRef.current.appendChild(container);
    thoughtDotsContainerRef.current = container;
    
    // Render the ThoughtDots component into the container
    const root = createRoot(container);
    root.render(<ThoughtDots />);

    // Force reflow to ensure proper layout
    if (scrollingAreaRef.current) {
      void scrollingAreaRef.current.offsetHeight; // Force reflow
    }

    // Smooth scroll to show the ThoughtDots
    setTimeout(() => {
      if (scrollingAreaRef.current && container) {
        // Get the container's position relative to the scrolling area
        const scrollingAreaRect = scrollingAreaRef.current.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const relativeTop = containerRect.top - scrollingAreaRect.top + scrollingAreaRef.current.scrollTop;
        
        scrollingAreaRef.current.scrollTo({
          top: relativeTop,
          behavior: 'smooth'
        });
      }
    }, 100);

    return container;
  }, []);

  /**
   * Removes ThoughtDots from the chat
   */
  const removeThoughtDots = useCallback(() => {
    if (thoughtDotsContainerRef.current && thoughtDotsContainerRef.current.parentNode) {
      thoughtDotsContainerRef.current.parentNode.removeChild(thoughtDotsContainerRef.current);
      thoughtDotsContainerRef.current = null;
    }
  }, []);

  /**
   * Appends an AI response to the chat and removes ThoughtDots
   * @param responseText - The AI response text to display
   */
  const appendAIResponse = useCallback((responseText: string) => {
    if (!scrollingAreaRef.current) return;

    // Remove ThoughtDots before showing response
    removeThoughtDots();

    // Create a new div container for the AI response AIResponse component
    const container = document.createElement('div');
    container.style.width = '100%';
    container.style.display = 'flex';
    container.style.justifyContent = 'flex-start';
    scrollingAreaRef.current.appendChild(container);
    
    // Render the AIResponse component into the container
    const root = createRoot(container);
    logger.info('Rendering AIResponse');
    root.render(<AIResponse responseText={responseText} />);

    // Force reflow to ensure proper layout
    if (scrollingAreaRef.current) {
      void scrollingAreaRef.current.offsetHeight; // Force reflow
    }

    // Smooth scroll to the top of the new response
    setTimeout(() => {
      if (scrollingAreaRef.current && container) {
        // Get the container's position relative to the scrolling area
        const scrollingAreaRect = scrollingAreaRef.current.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const relativeTop = containerRect.top - scrollingAreaRect.top + scrollingAreaRef.current.scrollTop;
        
        scrollingAreaRef.current.scrollTo({
          top: relativeTop,
          behavior: 'smooth'
        });
      }
    }, 100);
  }, [removeThoughtDots]);

  /**
   * Appends WorkCards to the chat
   * @param cards - Array of work card data to display
   */
  const appendWorkCards = useCallback((cards: Array<{href: string; imageSrc: string; imageAlt: string; title: string; linkText: string}>) => {
    if (!scrollingAreaRef.current) return;

    // Create a container for WorkCards
    const container = document.createElement('div');
    container.style.width = '100%';
    scrollingAreaRef.current.appendChild(container);
    
    // Render the WorkCards component into the container
    const root = createRoot(container);
    root.render(<WorkCards cards={cards} onGetInTouch={() => setCurrentView(3)} />);
  }, [setCurrentView]);



  return (
    <div className='view-chat view-2 flex flex-col h-full relative md:px-12 md:pt-2'>
      <h2 
        className="card-title-lg mb-3"
        style={titleStyle}
      >
        What are you solving for?
      </h2>
      
      {/* Scrolling Area START */}
      <div className="mt-4 flex-1 min-h-0 relative">
        <div 
          ref={scrollingAreaRef} 
          id='scrolling-area' 
          className={`h-full overflow-y-auto flex flex-col items-end chat-scroll-area ${isScrolling ? 'scrolling' : ''}`}
          onWheel={(e) => e.stopPropagation()}
        >
        {/* User Message - visible */}
        <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end', ...userMessageStyle }}>
          <UserMessage 
            variant="list"
            listTitle="I want to..."
            listItems={selectedOptions}
          />
        </div>

        {/* First AI Response - typing animation */}
        {typedText && (
          <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-start' }}>
            <AIResponse responseText={typedText} />
          </div>
        )}

        {/* Mark: Items dynamically added here */}

        </div>
      </div>

      {/* Suggested Responses - stagger in after input animation */}
      {showSuggestedResponses && showSuggestions && suggestedResponses.length > 0 && (
        <SuggestedResponses 
          suggestions={suggestedResponses} 
          onResponseClick={handleSuggestionClick} 
          selectedRowIds={selectedRowIds}
          getButtonStyle={getSuggestedButtonStyle}
        />
      )}
      
      {/* Scroll to bottom button */}
      {showScrollButton && (
        <button
          onClick={scrollToBottom}
          className="scroll-to-bottom-button absolute bottom-24 left-1/2 w-10 h-10 bg-[#FDFEF6] rounded-full shadow-2xl flex items-center justify-center hover:bg-gray-100 transition-all duration-300 border border-gray-300 z-50"
          style={{ 
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15), 0 0 15px rgba(0, 0, 0, 0.08)',
            ...scrollButtonStyle
          }}
          aria-label="Scroll to bottom"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 14L5 9L6.4 7.6L10 11.2L13.6 7.6L15 9L10 14Z" fill="currentColor"/>
          </svg>
        </button>
      )}
      {/* Scrolling Area END */}

      {/* Custom scrollbar styles */}
      <style jsx>{`
        .chat-scroll-area {
          /* Always reserve space for scrollbar to prevent layout shift */
          scrollbar-gutter: stable;
          scrollbar-width: thin; /* Firefox - always thin to reserve space */
          scrollbar-color: transparent transparent; /* Hide by default */
          -ms-overflow-style: none; /* IE and Edge */
        }
        
        .chat-scroll-area.scrolling {
          scrollbar-color: rgba(0, 0, 0, 0.3) transparent; /* Show when scrolling */
        }
        
        .chat-scroll-area::-webkit-scrollbar {
          width: 0.375rem;
        }
        
        .chat-scroll-area::-webkit-scrollbar-track {
          background: transparent;
        }
        
        .chat-scroll-area::-webkit-scrollbar-thumb {
          background: transparent;
          border-radius: 0.1875rem;
          transition: background 0.3s ease;
        }
        
        .chat-scroll-area.scrolling::-webkit-scrollbar-thumb {
          background: rgba(0, 0, 0, 0.3);
        }
        
        .chat-scroll-area.scrolling::-webkit-scrollbar-thumb:hover {
          background: rgba(0, 0, 0, 0.5);
        }
        
        /* Textarea scrollbar styles */
        .chat-textarea {
          scrollbar-width: thin;
          scrollbar-color: transparent transparent;
          -ms-overflow-style: none;
        }
        
        .chat-textarea::-webkit-scrollbar {
          width: 0.375rem;
        }
        
        .chat-textarea::-webkit-scrollbar-track {
          background: transparent;
        }
        
        .chat-textarea::-webkit-scrollbar-thumb {
          background: transparent;
          border-radius: 0.1875rem;
        }
        
        .chat-textarea:hover::-webkit-scrollbar-thumb {
          background: rgba(0, 0, 0, 0.2);
        }
        
        .chat-textarea:hover::-webkit-scrollbar-thumb:hover {
          background: rgba(0, 0, 0, 0.4);
        }
        
        /* Responsive textarea height */
        .chat-textarea-responsive {
          height: 3.5rem;
          padding-top: calc((3.5rem - 1.5em) / 2);
          padding-bottom: calc((3.5rem - 1.5em) / 2);
        }
        
        @media (min-width: 1024px) {
          .chat-textarea-responsive {
            height: 4.25rem;
            padding-top: calc((4.25rem - 1.5em) / 2);
            padding-bottom: calc((4.25rem - 1.5em) / 2);
          }
        }
      `}</style>

      {/* Chat Input - fade in after typing */}
      {showInput && (
        <div id="chat-input" className="" style={inputStyle}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex items-center lg:justify-end">
              {/* Gradient border wrapper */}
              <div 
                className="flex-1 flex items-center rounded-[1.75rem] lg:rounded-[2.125rem]"
                style={{
                  background: 'linear-gradient(to right, #FAB4FF, #FF6B9D)',
                  padding: '2px'
                }}
              >
                <div className="flex items-center w-full rounded-[1.625rem] lg:rounded-[2rem]" style={{ background: '#FDFEF6', position: 'relative' }}>
                  <textarea
                    ref={textareaRef}
                    id="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyDown={(e) => {
                      // On desktop, submit form when Enter is pressed (without Shift)
                      if (e.key === 'Enter' && !e.shiftKey && window.innerWidth >= 1024) {
                        e.preventDefault();
                        const form = e.currentTarget.closest('form') as HTMLFormElement;
                        if (form && message.trim()) {
                          form.requestSubmit();
                        }
                      }
                    }}
                    placeholder="Type here..."
                    className="flex-1 px-4 bg-transparent resize-none focus:outline-none placeholder:text-black/60 chat-textarea rounded-[1.625rem] lg:rounded-[2rem]"
                    style={{
                      border: 'none',
                      outline: 'none',
                      lineHeight: '1.5',
                      boxSizing: 'border-box',
                      overflowY: 'auto',
                      minHeight: '3.5rem',
                      paddingRight: '4.5rem'
                    }}
                    required
                  />
                  <button
                    type="submit"
                    disabled={!message.trim()}
                    className={`w-11 h-11 min-w-[2.75rem] min-h-[2.75rem] lg:min-w-[3.5rem] lg:min-h-[3.5rem] bg-red-600 text-white rounded-full flex items-center justify-center transition-colors duration-300 ease-out disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 ${!message.trim() ? '' : 'hover:bg-red-700'}`}
                    style={{
                      ...buttonStyle,
                      position: 'absolute',
                      right: '0.5rem',
                      bottom: '0.4rem'
                    }}
                  >
                    <ArrowUpRight 
                      className="w-10 h-10 text-white rotate-[-45deg]" 
                      strokeWidth={1}
                    />
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}